/* =========================================================================
 * THE HUNT WORKSPACE — where a rep actually finds a champion and records it.
 *
 * Brian: "✓ Queued — the hand hunt works requested records first. — when i
 * clicked on find champion this is what it output. I don't want it fucking
 * queued — I want it listed just as we've discussed."
 *
 * He is right and the old button was lying. It said FIND and it set a flag.
 *
 * ⚠️ THE CONSTRAINT, STATED SO NOBODY REBUILDS THE WRONG THING.
 *
 * Nothing running in this browser can search the web. The control that could
 * billed per record through a paid API and is deleted by policy — see
 * huntJob.js. So a finding comes from a PERSON looking, and the job of this
 * app is to make that take thirty seconds instead of ten minutes, and to keep
 * the result with its receipt.
 *
 * So the press gives the rep everything the record already knows, the search
 * composed for them, and the fields to record the answer the moment they have
 * it. The queue still happens underneath — the sweep is a useful backstop —
 * but it is no longer the OUTCOME, because "queued" is not an answer to
 * "who won".
 *
 * WHAT A REP NEEDS ON THE OTHER SIDE OF THIS. Brian: "rep should be able to
 * review this and do any inputs to either design a ring or step into a meeting
 * with the director of the team or coach or parent." That is why the story
 * fields are here and not behind another click: a champion's name alone
 * designs nothing and opens no conversation. The title wording is what gets
 * engraved; the coach is who the presentation line names; the roster is what
 * makes the ring personalisable at all.
 * ========================================================================= */

import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { requestHunt } from './huntJob.js'
import { huntQuery } from './huntQuery.js'
import { championIssues } from './ChampionCell.jsx'
import { searchHistory } from './searchHistory.js'
import { multiChampion } from './multiChampion.js'
import styles from './pursuit.module.css'

const STORY = [
  ['titleLine', 'Title wording, exactly as the source states it', 'This is what gets engraved. Getting it from the source is the only way it is right.'],
  ['runnerUp', 'Runner-up', ''],
  ['score', 'Final score', ''],
  ['venue', 'Venue', ''],
  ['headCoach', 'Head coach', 'The presentation line has no name without it.'],
  ['seasonRecord', 'Season record — say if it is postseason', 'A 16-3 playoff run is not a 53-22-7 season.'],
  ['rosterUrl', 'Championship-season roster URL', 'Rings are personalised per player. A club’s /roster link resolves to NEXT season after the offseason — find the one that names this season.'],
]

/* ⚠️ THE FORM KEY IS NOT THE RECORD KEY, FOR TWO OF THE SEVEN.
 *
 * The normalizer hangs the story on the record as `coach` and `record`; the
 * backend's alias table takes `headCoach` and `seasonRecord` (and `coach` and
 * `record`) on the way back in, so the WRITE was never wrong and nothing would
 * have failed loudly. The READ was: prefilling by form key silently found
 * nothing for exactly those two, so a record that already carried the coach and
 * the season record would have rendered them blank and counted them as gaps —
 * inviting a rep to go and re-find a fact that was already on the record.
 *
 * One map, used by both the prefill and the gap count, so they cannot disagree
 * with each other about what the record already knows. */
const READ_KEY = { headCoach: 'coach', seasonRecord: 'record' }
const known = (rec, k) => String((rec?.story || {})[READ_KEY[k] || k] || '').trim()

/* ⚠️ THE ROW AND THIS PANEL MUST SAY THE SAME NUMBER.
 *
 * Caught by opening the live record: the row read "needs 4 details" and the
 * panel, two inches away, read "7 details still missing". Both were true and
 * neither was wrong — the row counts what BLOCKS A RING (and groups score and
 * season record as one "result detail"), while the panel was counting every
 * blank box on its own form, including runner-up and venue, which no ring
 * waits on.
 *
 * A rep does not see two denominators, they see a system that cannot count.
 * So the panel stops counting and asks the row's own function. Same lesson as
 * READ_KEY above: two things that must agree should not be derived twice. */
const RING_BLOCKERS = new Set(['nosource', 'notitle', 'nostory', 'noroster', 'nocoach'])
const ringGaps = (rec) => championIssues(rec).filter((i) => RING_BLOCKERS.has(i.key))

export default function HuntWorkspace({ record, onResolved, onClose }) {
  /* ⚠️ A CROWNED RECORD IS NOT AN EMPTY FORM.
   *
   * Brian opened this on a record that already named Danica Rinard and was
   * asked to go find a champion. The panel rendered blank because it never
   * read the record it was given — so the work already done was invisible and
   * the rep's job looked like starting over.
   *
   * Prefilled, the same panel becomes the right tool for the other half of the
   * job: filling the gaps a ring needs on a champion already established. */
  const [champion, setChampion] = useState(String(record?.champion || record?.winningSchool || ''))
  const [sourceUrl, setSourceUrl] = useState(String(record?.championSourceUrl || record?.story?.sourceUrl || ''))
  const [eventDate, setEventDate] = useState(String(record?.confirmedRaw || '').slice(0, 10))
  const [story, setStory] = useState(() => Object.fromEntries(
    STORY.map(([k]) => [k, known(record, k)]).filter(([, v]) => v)
  ))
  const [reason, setReason] = useState('')
  const [state, setState] = useState({ status: 'idle' })

  const id = String(record?.id || '')
  const q = huntQuery(record)
  /* What the record arrived with, not what is in the form — so the panel keeps
   * saying "filling the gaps" while somebody edits, instead of flipping back to
   * "find" the moment they clear a field. */
  const wasCrowned = Boolean(String(record?.champion || record?.winningSchool || '').trim())
  /* ⚠️ DID ANYBODY ACTUALLY LOOK? Brian: "nothing is found and then it pushes
   * me to a page to input my own champion. it never says it searched."
   *
   * The record has carried the answer all along — the tag, the timestamp and
   * the attempt count — and this panel rendered none of it, so a record
   * searched six times and one nobody had touched looked identical: a blank
   * box. See searchHistory.js. */
  const hist = searchHistory(record)
  /* ⚠️ TELL THE HUNTER HOW MANY TO LOOK FOR. Recording one weight-class winner
   * in a field built for one champion is how nine get lost. */
  const multi = multiChampion(record)
  const gaps = ringGaps(record)

  /* Queue it as a BACKSTOP, silently. The sweep working this record later costs
   * nothing and might beat the rep to it; what it must not do is present itself
   * as the answer, which is what the old button did. A failure here is not
   * worth telling anybody about — the rep is already looking. */
  const [queued, setQueued] = useState(false)
  if (!queued && id) {
    setQueued(true)
    requestHunt(id).catch(() => { /* the backstop failing changes nothing */ })
  }

  const problems = []
  if (!champion.trim()) problems.push('a champion')
  if (!/^https?:\/\//i.test(sourceUrl.trim())) problems.push('a source URL that actually says so')
  if (reason.trim().length < 15) problems.push('a note of at least 15 characters — it is the audit trail')

  const record_ = async () => {
    setState({ status: 'saving' })
    try {
      const r = await api.championResolve({
        id, outcome: 'crowned',
        champion: champion.trim(),
        sourceUrl: sourceUrl.trim(),
        eventDate: eventDate || undefined,
        reason: reason.trim(),
        runId: 'cc-hunt-workspace',
        story: Object.fromEntries(Object.entries(story).filter(([, v]) => String(v || '').trim())),
      })
      setState({ status: 'done', result: r })
      onResolved && onResolved(id, { champion: champion.trim(), championSourceUrl: sourceUrl.trim() })
    } catch (err) {
      setState({ status: 'error', message: err?.body?.reason || err?.body?.problems?.join(' ') || err?.message || 'Could not record it.' })
    }
  }

  if (state.status === 'done') {
    return (
      <div className={styles.hwDone}>
        ✓ <b>{champion.trim()}</b> recorded with its source.
        {onClose && <button type="button" className={styles.hwLink} onClick={onClose}>close</button>}
      </div>
    )
  }

  return (
    <div className={styles.hw} onClick={(e) => e.stopPropagation()}>
      <div className={styles.hwHead}>
        <b>{wasCrowned ? `${champion || 'This champion'} — fill the gaps` : 'Find the champion'}</b>
        {onClose && <button type="button" className={styles.hwLink} onClick={onClose}>close</button>}
      </div>
      {wasCrowned && (
        <div className={styles.hwHint}>
          Already recorded{record?.championSourceUrl ? ' with a source' : ' — but with no source, which makes it a rumour until one is added'}.
          {gaps.length > 0
            ? ` Needs ${gaps.length} detail${gaps.length === 1 ? '' : 's'} before this is a ring — ${gaps.map((g) => g.label.replace(/^no /, '')).join(', ')}. Saving updates the record; it does not re-crown it.`
            : ' Nothing missing — this one is ready to build.'}
        </div>
      )}

      {/* ⚠️ WHAT WAS ALREADY TRIED — FIRST, BEFORE THE FORM.
        *
        * "We looked hard and no source names a winner" is a finding worth
        * acting on; "nobody has looked yet" is a queue position. Rendering
        * both as an empty box is what taught a rep the system does nothing. */}
      {multi && (
        <div className={styles.hwHistory} data-tone="tried">
          <b>This crowns {multi.expected}{multi.team ? ' plus a team title' : ''} — not one champion.</b>
          <div className={styles.hwHistoryWhy}>
            {multi.reason}. Record them as a SPLIT so each {multi.label} gets its own
            record and its own ring, rather than typing one name into a field built
            for one champion.
          </div>
        </div>
      )}

      {!wasCrowned && (
        <div className={styles.hwHistory} data-tone={hist.tone}>
          <b>{hist.headline}</b>
          {hist.detail ? <div className={styles.hwHistoryWhy}>{hist.detail}</div> : null}
        </div>
      )}

      {/* WHAT THE RECORD ALREADY KNOWS. The rep should not have to open the
        * drawer to find out what they are searching for. */}
      <div className={styles.hwKnown}>
        {[record?.sport, record?.level, record?.state, record?.seasonYear]
          .filter((x) => x && String(x).trim())
          .map((x) => <span key={String(x)} className={styles.hwChip}>{String(x)}</span>)}
      </div>

      {/* THE SEARCH, one click. Opening a search page costs nothing — this is
        * the free route, and it is the same one the hand hunt uses. */}
      {q ? (
        <div className={styles.hwSearch}>
          <a href={`https://www.google.com/search?q=${encodeURIComponent(q)}`} target="_blank" rel="noopener noreferrer" className={styles.hwGo}>
            Search “{q}” ↗
          </a>
          <span className={styles.hwHint}>
            {wasCrowned
              ? 'Use this to find the detail that is still missing — the exact title wording, the venue, the coach.'
              : 'Governing body first, then a major outlet.'} Read the dateline — search puts
            last season’s recap above this one, and a repeat champion passes every check
            while the score and venue are silently from the wrong year.
          </span>
        </div>
      ) : (
        <div className={styles.hwHint}>
          This record has no name to search on. Fix the record before hunting it.
        </div>
      )}

      <div className={styles.hwGrid}>
        <label className={styles.hwField}>
          <span>Champion *</span>
          <input value={champion} onChange={(e) => setChampion(e.target.value)} placeholder="Exactly as the source names them" />
        </label>
        <label className={styles.hwField}>
          <span>Source URL * — the page that says so</span>
          <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" />
        </label>
        <label className={styles.hwField}>
          <span>Day it was decided</span>
          {/* Not the listed date — the day the final was actually played. The
            * board measures the gap and says so, which is how a wrong record
            * date gets found instead of quietly propagating. */}
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
        </label>
      </div>

      <details className={styles.hwStory}>
        <summary>The story — what turns this into a ring or a meeting</summary>
        {STORY.map(([k, label, why]) => (
          <label key={k} className={styles.hwField}>
            <span>{label}</span>
            <input value={story[k] || ''} onChange={(e) => setStory((s) => ({ ...s, [k]: e.target.value }))} />
            {why ? <em className={styles.hwWhy}>{why}</em> : null}
          </label>
        ))}
        {/* A field nobody looked for is a step skipped, not a gap — same rule
          * the hand-hunt skill enforces. */}
        <p className={styles.hwHint}>
          Leave blank what the source does not say. Never fill one from memory, and never
          carry a value across from another season.
        </p>
      </details>

      <label className={styles.hwField}>
        <span>How you know * — the audit trail</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. governing body recap, 2026 season, dateline confirmed on the page" />
      </label>

      {problems.length > 0 && (champion || sourceUrl || reason) && (
        <div className={styles.hwNeeds}>Still needs {problems.join(', ')}.</div>
      )}
      {state.status === 'error' && <div className={styles.hwBad}>{state.message}</div>}

      <button type="button" className={styles.hwSave}
        disabled={problems.length > 0 || state.status === 'saving'}
        onClick={record_}>
        {state.status === 'saving' ? 'Saving…' : wasCrowned ? 'Save what you found' : 'Record this champion'}
      </button>
    </div>
  )
}
